/**
 * One `/auth/refresh` in flight at a time, ever.
 *
 * The dashboard fires several queries at once. When the access token expires
 * they all get 401 within a few milliseconds of each other, and the naive fix —
 * "on 401, refresh and retry" — sends N refreshes with the same cookie.
 *
 * That is not merely wasteful. The backend rotates the refresh token on every
 * use and treats a **reused** token as a stolen one: the second concurrent
 * refresh presents a token the first has already consumed, the server answers
 * `AUTH_REFRESH_REUSED`, and the whole session is revoked. The user is thrown
 * back to the login page for the crime of having three panels on screen.
 *
 * So the promise is shared, not the request: whoever arrives first starts it,
 * everyone else awaits the same promise, and it is cleared only once settled.
 */

export type TokenSetter = (accessToken: string, expiresInSeconds: number) => void;

interface RefreshDeps {
  /** Performs the actual POST. Injected so this module has no fetch of its own. */
  perform(): Promise<{ accessToken: string; expiresIn: number }>;
  onSuccess: TokenSetter;
  /** Called when refresh fails — the session is over and state must be cleared. */
  onFailure(error: unknown): void;
}

let deps: RefreshDeps | null = null;
let inFlight: Promise<string> | null = null;

export function configureRefresh(next: RefreshDeps): void {
  deps = next;
}

/** Test seam. Leaves `deps` alone; only the in-flight promise is dropped. */
export function __resetRefreshState(): void {
  inFlight = null;
}

/** True while a refresh is running — asserted by the single-flight test. */
export function isRefreshing(): boolean {
  return inFlight !== null;
}

/**
 * Returns a fresh access token, starting a refresh only if one is not already
 * running. Rejects if the refresh fails, and every concurrent caller sees the
 * same rejection.
 */
export async function refreshAccessToken(): Promise<string> {
  if (!deps) throw new Error('refreshCoordinator was never configured');

  if (inFlight) return inFlight;

  const current = deps;
  inFlight = (async () => {
    try {
      const result = await current.perform();
      current.onSuccess(result.accessToken, result.expiresIn);
      return result.accessToken;
    } catch (err) {
      current.onFailure(err);
      throw err;
    } finally {
      // Cleared in `finally`, not on success: leaving a rejected promise cached
      // would make every later request fail with a stale error.
      inFlight = null;
    }
  })();

  return inFlight;
}
